import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  SKELETON_APPEAR_DELAY_MS,
  SKELETON_CARD_COUNT,
  SKELETON_LEAVE_MS,
  SKELETON_PHASE_IDLE,
  SKELETON_REVEAL_MS,
  SkeletonPhase,
} from '../../exploreSkeleton';
import {
  PlayingReelFinale,
  ReelFinaleKind,
  getReelFinaleTiming,
  hasPlayedFullReelFinale,
  markFullReelFinalePlayed,
  selectReelFinale,
} from '../../slotReels';
import { SlotReelEngine, createSlotReelEngine } from './slotReelEngine';

/** Post-login Random Outcomes drawer is holding / revealing the cards (!2158). */
function loginCardsHeld(): boolean {
  if (typeof document === 'undefined') return false;
  const cl = document.documentElement.classList;
  return cl.contains('ro-cards-hold') || cl.contains('ro-cards-reveal');
}

function prefersReducedMotion(): boolean {
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      !!window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  } catch {
    return false;
  }
}

/** Which finale plays now that the data landed after the reels were up for `shownForMs`. */
export function resolveReelFinale(shownForMs: number): ReelFinaleKind {
  return selectReelFinale({
    reducedMotion: prefersReducedMotion(),
    loginCardsHeld: loginCardsHeld(),
    fullPlayedThisSession: hasPlayedFullReelFinale(),
    shownForMs,
  });
}

export type ExploreSkeletonPhaseOptions = {
  /**
   * Play the slot-reel finale when the data lands (default true). Pass false when the
   * loading ended for another reason (e.g. the drawer closed): plain cross-fade.
   */
  reelFinale?: boolean;
};

/**
 * Drives the skeleton's lifecycle from the existing "loading" boolean.
 *
 *  loading starts  → mounted now, fades in only if it is still loading after
 *                    SKELETON_APPEAR_DELAY_MS (fast / cached loads never show it).
 *  loading ends    → if it never showed: nothing happens (straight to the cards).
 *                    If it showed: the reel finale (top card lands check-check-check,
 *                    gold halo sweeps down, real cards come in behind it; full, short or
 *                    reduced, see slotReels.selectReelFinale), or when there is no finale
 *                    the old cross-fade: fade out (SKELETON_LEAVE_MS) while the real
 *                    cards fade in (`revealing`).
 *
 * `visible` stays true through the finale until the halo is most of the way down, so the
 * Insight + Timeframe sections (useExploreSectionsCollapsed) slide back in near its end.
 */
export function useExploreSkeletonPhase(
  loading: boolean,
  options?: ExploreSkeletonPhaseOptions
): SkeletonPhase {
  const [phase, setPhase] = useState<SkeletonPhase>(() =>
    loading ? { mounted: true, visible: false, revealing: false, finale: null } : SKELETON_PHASE_IDLE
  );
  const shownRef = useRef(false);
  const shownAtRef = useRef(0);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const finaleAllowedRef = useRef(true);
  finaleAllowedRef.current = options?.reelFinale !== false;

  useEffect(() => {
    if (loading) {
      // A new load during a finale: the reels are still on screen, keep them up.
      const duringFinale = !!phaseRef.current.finale && phaseRef.current.mounted;
      if (duringFinale) {
        shownRef.current = true;
        shownAtRef.current = Date.now();
      }
      setPhase((p) => ({
        mounted: true,
        visible: p.visible || duringFinale,
        revealing: false,
        finale: null,
      }));
      if (shownRef.current) return undefined;
      const appear = window.setTimeout(() => {
        shownRef.current = true;
        shownAtRef.current = Date.now();
        setPhase((p) => ({ ...p, visible: true }));
      }, SKELETON_APPEAR_DELAY_MS);
      return () => window.clearTimeout(appear);
    }

    if (!shownRef.current) {
      setPhase(SKELETON_PHASE_IDLE);
      return undefined;
    }
    shownRef.current = false;

    const kind = finaleAllowedRef.current
      ? resolveReelFinale(Date.now() - shownAtRef.current)
      : 'none';

    if (kind === 'none') {
      setPhase({ mounted: true, visible: false, revealing: true, finale: null });
      const unmount = window.setTimeout(
        () => setPhase((p) => ({ ...p, mounted: false })),
        SKELETON_LEAVE_MS
      );
      const settle = window.setTimeout(
        () => setPhase((p) => ({ ...p, revealing: false })),
        SKELETON_REVEAL_MS
      );
      return () => {
        window.clearTimeout(unmount);
        window.clearTimeout(settle);
      };
    }

    if (kind === 'full') markFullReelFinalePlayed();
    const timing = getReelFinaleTiming(kind);
    setPhase({ mounted: true, visible: true, revealing: false, finale: kind });
    const timers = [
      window.setTimeout(() => setPhase((p) => ({ ...p, revealing: true })), timing.haloStartMs),
      window.setTimeout(() => setPhase((p) => ({ ...p, visible: false })), timing.sectionsInMs),
      window.setTimeout(() => setPhase((p) => ({ ...p, mounted: false })), timing.unmountMs),
      window.setTimeout(
        () => setPhase((p) => ({ ...p, revealing: false, finale: null })),
        timing.revealEndMs
      ),
    ];
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [loading]);

  return phase;
}

/** Sparks that burst from the winning card (top card only). */
const SPARK_COUNT = 10;

type ExploreLoadingSkeletonProps = {
  /** Faded in (see useExploreSkeletonPhase). */
  visible: boolean;
  count?: number;
  /** Finale playing (useExploreSkeletonPhase); null while loading. */
  finale?: PlayingReelFinale | null;
  /** Current casino's logo for the reels (slotReels.pickCasinoLogoSrc); null leaves it out. */
  logoSrc?: string | null;
  /**
   * Slot-reel cards (true) or the original gray pulsing cards (false). MapStats passes
   * the casino's showLoadingReels (lib/showLoadingReels.ts), so a casino without
   * showLoadingReels = 1 gets the gray cards. Gray cards have no engine and no finale.
   */
  reels?: boolean;
};

/** The original (pre-reels) placeholder: a gray card pulsing between 50% and 20%. */
const GrayCard: React.FC = () => (
  <div className="explore-skeleton__card explore-skeleton__card--gray" />
);

const ReelCard: React.FC<{ index: number }> = ({ index }) => (
  <div className="explore-skeleton__card slot-reel-card">
    <div className="slot-reel-card__window">
      {[0, 1, 2].map((r) => (
        <div key={r} className="slot-reel">
          <div className="slot-reel__strip">
            <canvas className="slot-reel__sharp" />
            <canvas className="slot-reel__blur" />
          </div>
        </div>
      ))}
      <span className="slot-reel-card__payline slot-reel-card__payline--left" />
      <span className="slot-reel-card__payline slot-reel-card__payline--right" />
      <span className="slot-reel-card__shine" />
    </div>
    <div className="slot-reel-card__lines">
      <span className="explore-skeleton__line explore-skeleton__line--title" />
      <span className="explore-skeleton__line explore-skeleton__line--sub" />
      <span className="explore-skeleton__line-row">
        <span className="explore-skeleton__line explore-skeleton__line--stat" />
        <span className="explore-skeleton__line explore-skeleton__line--stat-wide" />
      </span>
    </div>
    {index === 0 && (
      <span className="slot-reel-card__sparks">
        {Array.from({ length: SPARK_COUNT }, (_, i) => (
          <span key={i} className="slot-reel-card__spark" />
        ))}
      </span>
    )}
  </div>
);

/**
 * Placeholder EGM cards for the Explore drawer's list area: each one a three-reel slot
 * machine beside pulsing placeholder text lines (`reels`), or the original gray pulsing
 * cards (`reels={false}`). Decorative (aria-hidden); the only thing assistive tech gets
 * is a visually hidden "Loading machines" status. No text, no data, no handlers. The
 * reels are driven by slotReelEngine (one rAF loop), created only for reel cards.
 */
const ExploreLoadingSkeleton: React.FC<ExploreLoadingSkeletonProps> = ({
  visible,
  count = SKELETON_CARD_COUNT,
  finale = null,
  logoSrc = null,
  reels = true,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<SlotReelEngine | null>(null);
  const logoRef = useRef(logoSrc);
  logoRef.current = logoSrc;

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !reels) return undefined;
    const engine = createSlotReelEngine(root, { logoSrc: logoRef.current });
    engineRef.current = engine;
    return () => {
      engine.destroy();
      engineRef.current = null;
    };
  }, [count, reels]);

  useEffect(() => {
    engineRef.current?.setLogoSrc(logoSrc);
  }, [logoSrc]);

  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    if (finale) engine.startFinale(finale);
    else engine.resetToLoading();
  }, [finale]);

  if (!reels) {
    // Exactly the pre-reels skeleton: gray cards, fade in, cross-fade out (no finale).
    return (
      <div
        className={clsx(
          'explore-skeleton',
          'explore-skeleton--gray',
          visible && 'explore-skeleton--visible'
        )}
        role="status"
        aria-live="polite"
        aria-busy="true"
        data-testid="explore-skeleton"
      >
        <span className="explore-skeleton__sr">Loading machines</span>
        <div className="explore-skeleton__cards" aria-hidden="true">
          {Array.from({ length: count }, (_, i) => (
            <GrayCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={clsx(
        'explore-skeleton',
        (visible || !!finale) && 'explore-skeleton--visible',
        finale && `explore-skeleton--finale explore-skeleton--finale-${finale}`
      )}
      role="status"
      aria-live="polite"
      aria-busy={finale ? 'false' : 'true'}
      data-testid="explore-skeleton"
    >
      <span className="explore-skeleton__sr">Loading machines</span>
      <div className="explore-skeleton__cards" aria-hidden="true">
        {Array.from({ length: count }, (_, i) => (
          <ReelCard key={i} index={i} />
        ))}
      </div>
      <span className="explore-skeleton__halo" aria-hidden="true" />
    </div>
  );
};

export default ExploreLoadingSkeleton;
